import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Star, 
  Loader, 
  CheckCircle, 
  XCircle, 
  AlertCircle
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useNotification } from '../../context/NotificationContext';

interface BookingCounts {
  total: number;
  pending: number;
  confirmed: number;
  completed: number;
  cancelled: number;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [bookingCounts, setBookingCounts] = useState<BookingCounts>({
    total: 0,
    pending: 0,
    confirmed: 0,
    completed: 0,
    cancelled: 0
  });

  useEffect(() => {
    const fetchBookingCounts = async () => {
      try {
        setLoading(true);
        
        // Get auth token for the request
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error('No session found');
        }

        // Fetch booking counts through the edge function
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-bookings`,
          {
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to fetch booking counts');
        }

        const bookings = await response.json();
        
        // Count bookings by status
        const counts: Record<string, number> = {
          pending: 0,
          confirmed: 0,
          completed: 0,
          cancelled: 0
        };
        
        bookings.forEach((booking: any) => {
          if (booking.status in counts) {
            counts[booking.status]++;
          }
        });

        // Set the booking counts
        setBookingCounts({
          total: bookings.length,
          pending: counts.pending || 0,
          confirmed: counts.confirmed || 0,
          completed: counts.completed || 0,
          cancelled: counts.cancelled || 0
        });
      } catch (error) {
        console.error('Error fetching booking counts:', error);
        showNotification('error', 'Failed to load booking statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchBookingCounts();
  }, [showNotification]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-8">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Total Bookings</h3>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{bookingCounts.total}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <h3 className="text-sm font-medium text-yellow-500 mb-2">Pending</h3>
          <p className="text-3xl font-bold text-yellow-600">{bookingCounts.pending}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <h3 className="text-sm font-medium text-green-500 mb-2">Confirmed</h3>
          <p className="text-3xl font-bold text-green-600">{bookingCounts.confirmed}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <h3 className="text-sm font-medium text-blue-500 mb-2">Completed</h3>
          <p className="text-3xl font-bold text-blue-600">{bookingCounts.completed}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <h3 className="text-sm font-medium text-red-500 mb-2">Cancelled</h3>
          <p className="text-3xl font-bold text-red-600">{bookingCounts.cancelled}</p>
        </div>
      </div>

      <div className="mt-8 bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Booking Status Breakdown</h2>
        <div className="relative h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          {bookingCounts.total > 0 && (
            <>
              <div 
                className="absolute h-full bg-yellow-500" 
                style={{ width: `${(bookingCounts.pending / bookingCounts.total) * 100}%` }}
              />
              <div 
                className="absolute h-full bg-green-500" 
                style={{ 
                  width: `${(bookingCounts.confirmed / bookingCounts.total) * 100}%`,
                  left: `${(bookingCounts.pending / bookingCounts.total) * 100}%`
                }}
              />
              <div 
                className="absolute h-full bg-blue-500" 
                style={{ 
                  width: `${(bookingCounts.completed / bookingCounts.total) * 100}%`,
                  left: `${((bookingCounts.pending + bookingCounts.confirmed) / bookingCounts.total) * 100}%`
                }}
              />
              <div 
                className="absolute h-full bg-red-500" 
                style={{ 
                  width: `${(bookingCounts.cancelled / bookingCounts.total) * 100}%`,
                  left: `${((bookingCounts.pending + bookingCounts.confirmed + bookingCounts.completed) / bookingCounts.total) * 100}%`
                }}
              />
            </>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-yellow-500 rounded-full" />
            <span className="text-sm text-gray-600 dark:text-gray-400">Pending ({bookingCounts.pending})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full" />
            <span className="text-sm text-gray-600 dark:text-gray-400">Confirmed ({bookingCounts.confirmed})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full" />
            <span className="text-sm text-gray-600 dark:text-gray-400">Completed ({bookingCounts.completed})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full" />
            <span className="text-sm text-gray-600 dark:text-gray-400">Cancelled ({bookingCounts.cancelled})</span>
          </div>
        </div>
      </div>
    </div>
  );
}